/**
 * Natures de ticket (`category`, ce que le CLIENT déclare) pour la file du
 * back-office.
 *
 * Les libellés sont servis par `GET /admin/support/tickets/options`
 * (`categories`) ; la liste ci-dessous n'est qu'un REPLI, utilisé tant que la
 * réponse n'est pas arrivée ou face à une API antérieure au 14/10/2026 qui ne
 * les servait pas encore. Sans lui, la file afficherait des clés brutes
 * (« EVOLUTION ») le temps d'un chargement.
 */

export interface NatureTicket {
  key: string;
  label: string;
}

export const NATURES_PAR_DEFAUT: NatureTicket[] = [
  { key: 'BUG', label: 'Dysfonctionnement' },
  { key: 'EVOLUTION', label: "Demande d'évolution" },
  { key: 'UTILISATION', label: 'Utilisation' },
  { key: 'FACTURATION', label: 'Facturation' },
  { key: 'FISCAL', label: 'Fiscal / social' },
  { key: 'AUTRE', label: 'Autre' },
];

/** Les natures à proposer : celles du serveur si elles sont là, sinon le repli. */
export function naturesDisponibles(servies: NatureTicket[] | null | undefined): NatureTicket[] {
  return Array.isArray(servies) && servies.length > 0 ? servies : NATURES_PAR_DEFAUT;
}

/** Libellé d'une nature ; la clé brute pour une valeur inconnue (jamais vide). */
export function libelleNature(
  cle: string | null | undefined,
  natures?: NatureTicket[] | null,
): string {
  if (!cle) return '—';
  return naturesDisponibles(natures).find((n) => n.key === cle)?.label ?? cle;
}

/**
 * Classe CSS du badge de nature.
 *
 * Seules deux natures ont une teinte propre : `BUG` (un incident) et
 * `EVOLUTION` (une idée) — ce sont les deux que la file doit distinguer d'un
 * coup d'œil. Les autres gardent le badge neutre : colorer six natures, c'est
 * n'en faire ressortir aucune.
 */
export function classeNature(cle: string | null | undefined): string {
  const base = 'atk-nature';
  if (cle === 'EVOLUTION') return `${base} atk-nature--evolution`;
  if (cle === 'BUG') return `${base} atk-nature--bug`;
  return base;
}

/** Filtres de la file, tels que l'écran les tient. */
export interface FiltresFile {
  status?: string;
  product?: string;
  q?: string;
  assigned?: string;
  menu?: string;
  type?: string;
  category?: string;
}

/**
 * Paramètres de `GET /admin/support/tickets`. Un filtre vide n'est pas envoyé :
 * « toutes les natures » n'est pas une nature.
 */
export function parametresFile(f: FiltresFile): URLSearchParams {
  const params = new URLSearchParams();
  const q = f.q?.trim();
  if (f.status) params.set('status', f.status);
  if (f.product) params.set('product', f.product);
  if (q) params.set('q', q);
  if (f.assigned) params.set('assigned', f.assigned);
  if (f.menu) params.set('menu', f.menu);
  if (f.type) params.set('type', f.type);
  if (f.category) params.set('category', f.category);
  return params;
}
